/**
 * Shared "assignee chip with dropdown" picker.
 *
 * Originally lived inside `WbsItemDetailPanel.tsx`; pulled out so the
 * migration-task detail panel can render the exact same UI (color chip
 * trigger + popover with one row per assignee + "未割当" clear option).
 * Centralising it means visual tweaks (chip size, popover width, hover
 * states) ship to both surfaces in lock-step.
 *
 * Consumers wire the actual Firestore write in `onChange` — this
 * component only owns the open / close state and the visual chip.
 */
import { useEffect, useRef, useState } from 'react';
import type { AssigneeDoc } from '@mct/shared';
import { AssigneeChip } from './AssigneeChip';
import { cn } from '../../lib/utils';

export interface AssigneeChipPickerProps {
  /** Project-wide assignee catalog. Empty array renders only the
   *  "未割当" clear option, which matches the WBS empty-catalog UX. */
  assignees: readonly AssigneeDoc[];
  /** Currently selected assigneeId. Empty string means "unassigned". */
  assigneeId: string;
  /** Resolved doc for the current assigneeId, or null when unassigned.
   *  Passed in (rather than re-resolved here) so callers can centralise
   *  the lookup with the rest of their detail-panel state. */
  currentAssignee: AssigneeDoc | null;
  /** Callback fired when the user picks a different assignee.
   *  Receives the new assigneeId, or `''` for "unassigned". */
  onChange: (nextId: string) => void | Promise<void>;
  /** Optional label rendered before the chip (e.g. "担当:"). Pass `null`
   *  to omit; defaults to "担当:" so the WBS detail panel keeps its
   *  current look. */
  label?: string | null;
  /** Optional `data-testid` override on the wrapping root. Defaults to
   *  the WBS-original "wbs-detail-assignee-picker" so existing tests in
   *  the WBS suite keep matching. Migration callers pass their own. */
  testId?: string;
}

export function AssigneeChipPicker({
  assignees,
  assigneeId,
  currentAssignee,
  onChange,
  label = '担当:',
  testId = 'wbs-detail-assignee-picker',
}: AssigneeChipPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onDown);
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      className="relative inline-flex items-center gap-1"
      data-testid={testId}
    >
      {label ? (
        <span className="text-[11px] text-slate-500">{label}</span>
      ) : null}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="担当を変更"
        className="inline-flex items-center"
      >
        <AssigneeChip assignee={currentAssignee} />
      </button>
      {open ? (
        <div className="absolute left-0 top-full z-30 mt-1 max-h-72 w-56 overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          <button
            type="button"
            className={cn(
              'block w-full px-2 py-1 text-left text-xs hover:bg-slate-50',
              assigneeId === '' ? 'bg-slate-100' : '',
            )}
            onClick={() => {
              setOpen(false);
              void onChange('');
            }}
          >
            <span className="italic text-slate-500">— 未割当 —</span>
          </button>
          {assignees.map((a) => (
            <button
              key={a.assigneeId}
              type="button"
              className={cn(
                'flex w-full items-center gap-2 px-2 py-1 text-left text-xs hover:bg-slate-50',
                assigneeId === a.assigneeId ? 'bg-slate-100' : '',
              )}
              onClick={() => {
                setOpen(false);
                void onChange(a.assigneeId);
              }}
            >
              <AssigneeChip assignee={a} />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
